import { DevModeComponent } from "@me8eon/devmode";
import { scroll, useAllSearchData, useCombinedAggregates, useTriggerSearch } from "./search";
import { useCommonComponents } from "@me8eon/components";
import React, { useMemo, useState } from "react";
import { useAttributeEditorComponents, useEditors } from "@me8eon/editors";
import { GetterSetter, makeGetterSetter } from "@me8eon/context";
import { useAttributeValueComponents, useRenderers } from "@me8eon/renderers";
import { camelCaseToWords, nameWithOptions } from "@me8eon/string_utils";
import { SimpleDisplayFilters, useGuiFilters } from "@me8eon/filters";
import { lensBuilder } from "@me8eon/optics";
import { FeatureFlags, useFeatureFlags } from "@me8eon/featureflags";
import { useDataSources } from "@me8eon/data_sources";
import { useSelectedDataView } from "@me8eon/data_view_filter";
import { useDebug } from "@me8eon/debug";
import { searchDebugName } from "@me8eon/filter_data_widget";
import { elasticSearch } from "@me8eon/elastic_search_data_sources";
import { useSearchParser } from "./search.parser";
import { normaliseWidgetDefns } from "@me8eon/data_views";


export type SelectedDebugProps = {
    debug: any,
    selectedAspect: string,
    selectedDebugOps: GetterSetter<string>,
    name: string,
    rootId: string
};

function SelectedDebug({
                           debug,
                           selectedAspect,
                           selectedDebugOps,
                           name,
                           rootId,
                       }: SelectedDebugProps) {
    const debugLog = useDebug(searchDebugName);
    const options = ["results", "filter", "query", "scroll", "debug"];
    const selected = selectedDebugOps[0];
    const { Json: JustJson } = useRenderers();
    const { SelectableButtonBar, ClipHeight } = useCommonComponents();
    const ds = useDataSources();
    const allSearchOps = useAllSearchData();
    const [allSearch, setAllSearch] = allSearchOps;
    const searchResult = allSearch[name]?.searchResults?.[selectedAspect];
    const dataView = useSelectedDataView();
    const details = dataView.search[name];
    const detail: any = details?.dataSource?.find((d: any) => d.type === elasticSearch);
    const { DataLayout, Text, Json } = useAttributeValueComponents();
    const newRootId = `${rootId}.debug.${selected}`;

    function scrollClick() {
        if (detail)
            scroll(ds, name, detail as any, allSearchOps, debugLog);
    }

    const count = (searchResult as any)?.value?.results?.length;
    const finished = (searchResult as any)?.value?.finished;
    const selectedDebugData = debug[selectedAspect];
    return (
        <ClipHeight maxHeight="400px" scrollable={true}>
            <SelectableButtonBar
                rootId="devmode.search.aspect"
                options={options}
                selectedOps={selectedDebugOps}
            />
            {detail && <button onClick={scrollClick}>Scroll</button>}
            {selected === "filter" &&
                <JustJson rootId={rootId} attribute={`${rootId}.debug.${selectedAspect}`} value={selectedDebugData?.filters} />}
            {selected === "query" &&
                <JustJson rootId={rootId} attribute={`${rootId}.debug.${selectedAspect}`} value={selectedDebugData?.query} />}
            {selected === "results" &&
                <>
                    <br />Count {count} / {finished?.toString() || "undefined"}
                    <ClipHeight maxHeight="320px" scrollable={true}>
                        <JustJson rootId={rootId} attribute={`${rootId}.debug.${selectedAspect}`} value={searchResult} />
                    </ClipHeight></>}
            {selected === "scroll" &&
                <DataLayout rootId={newRootId} layout={[2, 2, 1, 1]}>
                    <Text rootId={newRootId} attribute={"devmode.search.count"} value={count?.toString()} />
                    <Text rootId={newRootId} attribute={"devmode.search.finished"} value={finished?.toString()} />
                    <Text rootId={newRootId} attribute={"devmode.search.pageInDebug"} value={selectedDebugData?.page?.toString()} />
                    <Text rootId={newRootId} attribute={"devmode.search.pageInResult"} value={(searchResult as any)?.value?.page?.toString()} />
                    <Json rootId={newRootId} attribute={"devmode.search.paging"} value={(searchResult as any)?.value?.paging} />
                </DataLayout>}
            {selected === "debug" &&
                <ClipHeight maxHeight="320px" scrollable={true}>
                    <JustJson rootId={rootId} attribute={`${rootId}.debug.${selectedAspect}`} value={selectedDebugData} />
                </ClipHeight>}
            <></>
        </ClipHeight>
    );
}

export type SelectedAspectProps = {
    aspects: string[]
    selectedAspectOps: GetterSetter<string>,
    selectedDebugOps: GetterSetter<string>,
    debug: any,

    name: string,
    rootId: string
};

function SelectedAspect({
                            aspects,
                            selectedAspectOps,
                            selectedDebugOps,
                            debug,
                            name,
                            rootId,
                        }: SelectedAspectProps) {
    const { Json } = useRenderers();
    const [allSearch, setAllSearch] = useAllSearchData();
    const thisSearch = allSearch[name];
    const [selectedAspect] = selectedAspectOps;
    const valid = aspects.includes(selectedAspect);
    const { ClipHeight } = useCommonComponents();
    const aggs = useCombinedAggregates(name);
    if (selectedAspect === "all")
        return <ClipHeight maxHeight="400px" scrollable={true}><Json rootId={rootId} attribute={`${rootId}.filter`} value={thisSearch} /></ClipHeight>;
    if (selectedAspect === "aggs")
        return <Json rootId={rootId} attribute={"aggs"} value={aggs} />;
    if (valid) return <SelectedDebug
        debug={debug}
        selectedAspect={selectedAspect}
        selectedDebugOps={selectedDebugOps}
        name={name}
        rootId={rootId}
    />;
    return <div>Select above please. Aspects are {aspects.join(",")}. Selected is {selectedAspect}. Valid is {valid.toString()}</div>;
}

export const DevModeSearch: DevModeComponent = () => {
    const [allSearch] = useAllSearchData();
    const { TwoColumnAndRest, SelectableButtonBar } = useCommonComponents();
    const { H3 } = useRenderers();
    const { JsonEditor } = useEditors();
    const { OneLine } = useAttributeEditorComponents();

    const names = Object.keys(allSearch || {});
    const selectedOps = useState(names[0]);
    const guiFilterOps = useGuiFilters();

    const selectedSearchIndex = names.indexOf(selectedOps[0]);
    const name = names[selectedSearchIndex];
    const thisSearch = allSearch[name];
    const debug = thisSearch.debug;

    const aspects = useMemo(() => ["all", "aggs", ...Object.keys(debug || {})], [debug]);
    const selectedAspectOps = useState(aspects[0]);

    const searchTextOps: GetterSetter<string> = makeGetterSetter(guiFilterOps[0], guiFilterOps[1], lensBuilder<any>().focusOn("searchText"));
    const rootId = "devmode-search";

    const ffOps = useFeatureFlags();
    const [ff, setFF] = makeGetterSetter(ffOps[0], ffOps[1], lensBuilder<FeatureFlags>().focusOn("search").focusOn("value"));
    const selectedDebugOps = useState("results");

    const trigger = useTriggerSearch<any>();
    const selectedDataView = useSelectedDataView<string, any>();
    const searchDataSources = normaliseWidgetDefns(selectedDataView.widgets);
    const newFilterName = searchDataSources.find(x => x.widget.startsWith("newFilter"));
    const options = newFilterName ? nameWithOptions(newFilterName.widget).options || [] : [];
    const parser = useSearchParser();
    return (
        <div>
            <SelectableButtonBar rootId="devmode.search" options={names} selectedOps={selectedOps} />
            <TwoColumnAndRest rootId={rootId}>
                <>
                    <span>
                        <label htmlFor="do-search">Do Search</label>
                        <input
                            data-testid="do-search"
                            id="do-search"
                            type="checkbox"
                            checked={ff === true}
                            onChange={() => setFF(!ff)}
                        />
                    </span>
                    <H3 rootId={rootId} attribute="guifiltertitle" value="Gui Filters" />
                    <JsonEditor ops={guiFilterOps} rootId={rootId + ".guiFiltersEditor"} />
                    <OneLine rootId={rootId} attribute="searchText" ops={searchTextOps} />
                    <SimpleDisplayFilters rootId={rootId} filtersOps={guiFilterOps} options={options} />
                </>
                <>
                    <button onClick={() => trigger(name, guiFilterOps[0])}>Copy Gui Filters to {camelCaseToWords(name)}</button>

                    <SelectableButtonBar rootId="devmode.search.aspect" options={aspects} selectedOps={selectedAspectOps} />
                    <SelectedAspect
                        aspects={aspects}
                        selectedAspectOps={selectedAspectOps}
                        selectedDebugOps={selectedDebugOps}
                        debug={debug}
                        name={name}
                        rootId={rootId}
                    />
                </>
            </TwoColumnAndRest>
        </div>
    );
};
